import React from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {pluralize} from "@rw/string_utils";

import {IStore} from "../../app/reducers/hybrid_reducer";
import {getVoivodeshipName, Voivodeship} from "../../app/types/locationTypes";
import {getOfferListUrl} from "../../offer/list/url_utils/get_offer_list_url";
import {UMakeKeys} from "../../offer/list/url_utils/tag_utils";
import {getMakeSlug} from "../../offer/utils/get_make_slug";
import {Container} from "./CarBrandsSection";

const featuredMakeSlugs = new Set(["volkswagen", "volvo", "audi", "bmw", "toyota", "skoda", "peugeot", "citroen", "ford", "mercedesbenz"]);
const pluralOffer = pluralize(["oferta", "oferty", "ofert"]);

export const BrowseCarsSection: React.FC = () => {
    const makes = useSelector((store: IStore) => store.carList.cars.allMakes);
    const topLocations = useSelector((store: IStore) => store.carList.carsTopLocations);

    const cityLinks = Object.entries(topLocations || {})
        .flatMap(([region, cities]) => cities.map((city) => ({...city, region})))
        .filter((city) => city.offer_count > 0)
        .sort((a, b) => b.offer_count - a.offer_count)
        .slice(0, 6);

    const regionLinks = Object.entries(topLocations || {})
        .map(([region, cities]) => ({
            region,
            offerCount: cities.reduce((sum, city) => sum + city.offer_count, 0)
        }))
        .filter(({offerCount}) => offerCount > 0)
        .sort((a, b) => b.offerCount - a.offerCount)
        .slice(0, 6);

    const makeLinks = (makes || [])
        .map((make) => ({...make, slug: getMakeSlug(make.make)}))
        .filter((make): make is typeof make & {slug: UMakeKeys} => Boolean(make.slug) && make.count > 0 && !featuredMakeSlugs.has(make.slug))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

    if (cityLinks.length === 0 && regionLinks.length === 0 && makeLinks.length === 0) {
        return null;
    }

    return (
        <section css={sectionStyle} aria-labelledby="browse-cars-title">
            <Container>
                <div css={sectionHeading}>
                    <span css={eyebrow}>Znajdź właściwą drogę do auta</span>
                    <h2 id="browse-cars-title">Przeglądaj samochody po swojemu</h2>
                    <p>Wybierz popularne miasto, region lub markę i przejdź prosto do aktualnych ofert.</p>
                </div>

                <nav css={browseGrid} aria-label="Przeglądaj ogłoszenia samochodowe">
                    {cityLinks.length > 0 && (
                        <div css={[browsePanel, featuredPanel]}>
                            <div css={panelHeading}>
                                <span css={panelMarker}>01</span>
                                <div>
                                    <h3>Popularne miasta</h3>
                                    <p>Oferty najbliżej Ciebie</p>
                                </div>
                            </div>
                            <ul css={linkList}>
                                {cityLinks.map((city) => (
                                    <li key={`${city.region}-${city.citySlug}`}>
                                        <a
                                            href={getOfferListUrl({
                                                offerType: "osobowe",
                                                location: city.region,
                                                subLocation: city.citySlug
                                            })}
                                        >
                                            <span>{city.city}</span>
                                            <small>
                                                {city.offer_count} {pluralOffer(city.offer_count)}
                                            </small>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {regionLinks.length > 0 && (
                        <div css={browsePanel}>
                            <div css={panelHeading}>
                                <span css={panelMarker}>02</span>
                                <div>
                                    <h3>Samochody w regionie</h3>
                                    <p>Przeglądaj według województwa</p>
                                </div>
                            </div>
                            <ul css={linkList}>
                                {regionLinks.map(({region, offerCount}) => (
                                    <li key={region}>
                                        <a href={getOfferListUrl({offerType: "osobowe", location: region})}>
                                            <span>{getVoivodeshipName(region as Voivodeship) || region}</span>
                                            <small>
                                                {offerCount} {pluralOffer(offerCount)}
                                            </small>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {makeLinks.length > 0 && (
                        <div css={browsePanel}>
                            <div css={panelHeading}>
                                <span css={panelMarker}>03</span>
                                <div>
                                    <h3>Więcej popularnych marek</h3>
                                    <p>Odkryj pozostałe ogłoszenia</p>
                                </div>
                            </div>
                            <ul css={linkList}>
                                {makeLinks.map((make) => (
                                    <li key={make.slug}>
                                        <a href={getOfferListUrl({offerType: "osobowe", type: make.slug})}>
                                            <span>{make.make}</span>
                                            <small>
                                                {make.count} {pluralOffer(make.count)}
                                            </small>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </nav>

                <div css={allOffersLink}>
                    <a href={getOfferListUrl({offerType: "osobowe"})}>Zobacz wszystkie samochody</a>
                </div>
            </Container>
        </section>
    );
};

const sectionStyle = (theme: Theme) => css`
    padding: 52px 20px 60px;
    background: ${theme.colors.gray_light};
`;

const sectionHeading = (theme: Theme) => css`
    max-width: 680px;
    margin-bottom: 28px;

    h2 {
        margin: 6px 0 8px;
        color: ${theme.colors.brand_black};
        font-size: 2.8rem;
        line-height: 1.2;
    }

    p {
        margin: 0;
        color: ${theme.colors.gray_darker};
        font-size: 1.5rem;
    }
`;

const eyebrow = (theme: Theme) => css`
    color: ${theme.colors.brand_primary};
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const browseGrid = (theme: Theme) => css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media (min-width: ${theme.breakpoints.screen_sm}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (min-width: ${theme.breakpoints.screen_md}) {
        grid-template-columns: 1.15fr 1fr 1fr;
    }
`;

const browsePanel = (theme: Theme) => css`
    padding: 22px;
    border: 1px solid ${theme.colors.gray_very_dark};
    border-radius: 10px;
    background: ${theme.colors.brand_white};
`;

const featuredPanel = (theme: Theme) => css`
    border-top: 3px solid ${theme.colors.brand_primary};
`;

const panelHeading = (theme: Theme) => css`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;

    h3 {
        margin: 0 0 3px;
        color: ${theme.colors.brand_black};
        font-size: 1.7rem;
        line-height: 1.25;
    }

    p {
        margin: 0;
        color: ${theme.colors.gray_darker};
        font-size: 1.2rem;
    }
`;

const panelMarker = (theme: Theme) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 34px;
    height: 34px;
    border-radius: 50%;
    color: ${theme.colors.brand_primary};
    background: ${theme.colors.brand_primary_lighter};
    font-size: 1.1rem;
    font-weight: 700;
`;

const linkList = (theme: Theme) => css`
    display: grid;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;

    li + li {
        border-top: 1px solid ${theme.colors.gray_very_bright};
    }

    a {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 4px;
        color: ${theme.colors.gray_dark};
        text-decoration: none;
        transition: color 0.2s ease;
    }

    a:hover {
        color: ${theme.colors.brand_primary};
        text-decoration: none;
    }

    span {
        font-size: 1.4rem;
        font-weight: 600;
    }

    small {
        color: ${theme.colors.gray_warm};
        font-size: 1.1rem;
        white-space: nowrap;
    }
`;

const allOffersLink = (theme: Theme) => css`
    margin-top: 22px;
    text-align: right;

    a {
        color: ${theme.colors.brand_primary};
        font-size: 1.4rem;
        font-weight: 700;
        text-decoration: none;
    }

    a:hover {
        text-decoration: underline;
    }
`;
